import React, { createContext, useContext, useEffect, useState } from "react";

/**
 * Whether any assistant text bubble is still painting itself.
 *
 * `Text` reveals a finished response word by word, entirely client-side, long
 * after the text arrived. The approval chips are on a different path — they
 * read `aiChat.nextAction` and know nothing about the animation — so they
 * rendered while the proposal they belong to was still half-drawn, and a
 * merchant could approve something they had not been shown.
 *
 * This is the coupling, kept deliberately small: `Text` reports that it is
 * mid-reveal, and the composer's suggestion strip waits. It fails OPEN — with
 * no provider, or once every reveal has finished or unmounted, nothing is
 * revealing and the chips render as before. Nothing gates on it staying true.
 */
type RevealingIds = ReadonlySet<string>;

const RevealingContext = createContext(false);

/**
 * The setter is published separately from the flag, and as the raw `useState`
 * dispatch, because it must be referentially stable: `useReportTextReveal`
 * lists it as an effect dependency, and a setter re-created each render would
 * re-run the effect — cleanup included — on every state change it caused,
 * which is an infinite loop rather than a re-render.
 */
const SetRevealingIdsContext = createContext<React.Dispatch<
  React.SetStateAction<RevealingIds>
> | null>(null);

const withId = (
  current: RevealingIds,
  id: string,
  revealing: boolean
): RevealingIds => {
  if (current.has(id) === revealing) return current;
  const next = new Set(current);
  if (revealing) next.add(id);
  else next.delete(id);
  return next;
};

export const TextRevealProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  // Keyed by part rather than counted, so a double-report or a missed cleanup
  // cannot leave the strip hidden on a phantom reveal.
  const [ids, setIds] = useState<RevealingIds>(() => new Set());

  return (
    <SetRevealingIdsContext.Provider value={setIds}>
      <RevealingContext.Provider value={ids.size > 0}>
        {children}
      </RevealingContext.Provider>
    </SetRevealingIdsContext.Provider>
  );
};

/** True while any assistant bubble is still revealing. False with no provider. */
export const useIsTextRevealing = (): boolean => useContext(RevealingContext);

/** Reports one bubble's reveal state. A no-op with no provider. */
export const useReportTextReveal = (id: string, revealing: boolean) => {
  const setIds = useContext(SetRevealingIdsContext);

  useEffect(() => {
    if (!setIds) return;
    setIds((current) => withId(current, id, revealing));
    return () => setIds((current) => withId(current, id, false));
  }, [id, revealing, setIds]);
};
